import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';

export interface WhatsAppAutomationRule {
  id: string;
  name: string;
  entity_type: 'deal' | 'project' | 'ticket' | 'lead' | 'client';
  trigger_event: 'create' | 'update' | 'delete' | 'status_change' | 'state_transition' | 'api_trigger';
  trigger_status?: string | null; // Optional specific status to trigger on (only for status_change events)
  config_source: 'user' | 'global'; // Which WhatsApp config to use
  from_state_id?: string | null; // For state_transition: source state
  to_state_id?: string | null; // For state_transition: target state
  api_triggered?: boolean; // Whether this can be triggered via API
  api_webhook_url?: string | null; // Unique webhook URL for API triggers
  message_template: string;
  recipient_type: 'client' | 'assigned_user';
  active: boolean;
  created_at: string;
  updated_at: string;
  organization_id: string;
}

interface WhatsAppAutomationStore {
  rules: WhatsAppAutomationRule[];
  loading: boolean;
  error: string | null;
  fetchRules: () => Promise<void>;
  createRule: (rule: Omit<WhatsAppAutomationRule, 'id' | 'created_at' | 'updated_at' | 'organization_id'>) => Promise<WhatsAppAutomationRule | null>;
  updateRule: (id: string, rule: Partial<WhatsAppAutomationRule>) => Promise<WhatsAppAutomationRule | null>;
  deleteRule: (id: string) => Promise<boolean>;
  toggleRuleActive: (id: string, currentActive: boolean) => Promise<boolean>;
}

export const useWhatsAppAutomationStore = create<WhatsAppAutomationStore>((set, get) => ({
  rules: [],
  loading: false,
  error: null,

  fetchRules: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch rules for this organization
      const { data, error } = await supabase
        .from('whatsapp_automation_rules')
        .select('*')
        .eq('organization_id', userProfile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ rules: data || [] });
    } catch (error) {
      console.error('Error fetching WhatsApp automation rules:', error);
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createRule: async (rule) => {
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Create new rule
      const { data, error } = await supabase
        .from('whatsapp_automation_rules')
        .insert({
          ...rule,
          organization_id: userProfile.organization_id
        })
        .select()
        .single();

      if (error) throw error;
      
      const rules = get().rules;
      set({ rules: [data, ...rules] });
      return data;
    } catch (error) {
      console.error('Error creating WhatsApp automation rule:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateRule: async (id, rule) => {
    try {
      const { data, error } = await supabase
        .from('whatsapp_automation_rules')
        .update(rule)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const rules = get().rules.map(r => 
        r.id === id ? { ...r, ...data } : r
      );
      set({ rules });
      return data;
    } catch (error) {
      console.error('Error updating WhatsApp automation rule:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteRule: async (id) => {
    try {
      const { error } = await supabase
        .from('whatsapp_automation_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const rules = get().rules.filter(r => r.id !== id);
      set({ rules });
      return true;
    } catch (error) {
      console.error('Error deleting WhatsApp automation rule:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  toggleRuleActive: async (id, currentActive) => {
    try {
      const { error } = await supabase
        .from('whatsapp_automation_rules')
        .update({ active: !currentActive })
        .eq('id', id);

      if (error) throw error;

      const rules = get().rules.map(r => 
        r.id === id ? { ...r, active: !currentActive } : r
      );
      set({ rules });
      return true;
    } catch (error) {
      console.error('Error toggling WhatsApp automation rule status:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  }
}));